import {useMemo, useContext} from 'react'
import {Empty, Space, Table, Typography} from 'antd'
import {DeleteOutlined, DownloadOutlined, EditOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'
import { NavContext } from '../../../context/global_context'
import { notifyError } from '../../../utils/notifications'
import {downloadFile} from '../../../utils/common'

const DeviceTable = ({
  dataSource,
  pagination = false,
  getDevicesData = null,
  onEdit = null,
  onDelete = null,
}) => {
  const intl = useIntl()
  const [, navDispatch] = useContext(NavContext)

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'devices.name' }),
        dataIndex: 'name',
        width: '15%',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'devices.category' }),
        dataIndex: 'category',
        width: 160,
        sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'devices.description' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'description',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
          {intl.formatMessage({ id: 'devices.alias' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'alias_value',
        width: '15%',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'devices.action' }),
        align: 'right',
        width: 136,
        render: (_, record) => {
          const actions = [
            { key: 'edit', label: intl.formatMessage({ id: 'forms.edit' }), icon: <EditOutlined />, onClick: () => onEdit(record) },
          ]
          if (record.svg) {
            actions.push(
              { key: 'download_svg', label: intl.formatMessage({ id: 'devices.downloadSvg' }), icon: <DownloadOutlined />, onClick: async () => {
        const saved = await downloadFile(record.svg, `${record.name}.svg`)
        if (!saved) {
          notifyError(navDispatch, intl.formatMessage({ id: 'devices.downloadSvgError' }))
        }
      } },
            )
          }
          actions.push(
            { key: 'delete', label: intl.formatMessage({ id: 'forms.delete' }), icon: <DeleteOutlined />, danger: true, onClick: () => onDelete(record) },
          )
          return <TableActions actions={actions} />
        },
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `device-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        tableLayout='fixed'
        scroll={{
          x: dataSource.length > 0 ? 710 : undefined
        }}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getDevicesData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default DeviceTable
